/**
 * Limsa Club — Cross-Browser Compatibility Layer
 * Targets: Safari (iOS + macOS), Firefox, Chrome, Opera, Samsung Browser
 * Patterns: feature detection, graceful degradation, try/catch everywhere
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     1. BROWSER / OS DETECTION
     ───────────────────────────────────────────── */
  var ua = navigator.userAgent || '';
  var env = {
    ios:       /iP(hone|ad|od)/i.test(ua),
    android:   /Android/i.test(ua),
    safari:    /Safari/i.test(ua) && !/Chrome/i.test(ua),
    firefox:   /Firefox/i.test(ua),
    chrome:    /Chrome/i.test(ua) && !/Edge/i.test(ua),
    opera:     /OPR|Opera/i.test(ua),
    samsung:   /SamsungBrowser/i.test(ua),
    mobile:    /Mobi|Android|iPhone|iPad/i.test(ua),
    touchDevice: ('ontouchstart' in window) || navigator.maxTouchPoints > 0
  };

  /* ─────────────────────────────────────────────
     2. CSS FEATURE DETECTION → body classes
     ───────────────────────────────────────────── */
  try {
    var root = document.documentElement;
    if (env.ios)     root.classList.add('limsa-ios');
    if (env.android) root.classList.add('limsa-android');
    if (env.safari)  root.classList.add('limsa-safari');
    if (env.firefox) root.classList.add('limsa-firefox');
    if (env.mobile)  root.classList.add('limsa-mobile');

    // CSS backdrop-filter support
    if (!CSS.supports('backdrop-filter', 'blur(1px)') &&
        !CSS.supports('-webkit-backdrop-filter', 'blur(1px)')) {
      root.classList.add('no-backdrop-filter');
    }
    // CSS dvh support
    if (!CSS.supports('height', '1dvh')) {
      root.classList.add('no-dvh');
    }
    // CSS gap in flex support (old Safari <14)
    if (!CSS.supports('gap', '1px')) {
      root.classList.add('no-flex-gap');
    }
  } catch (e) {}

  /* ─────────────────────────────────────────────
     3. iOS VIEWPORT HEIGHT FIX
     100vh includes browser chrome on iOS Safari.
     We set --limsa-vh to the real visible height.
     ───────────────────────────────────────────── */
  try {
    function setVh() {
      var vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--limsa-vh', vh + 'px');
    }
    setVh();
    window.addEventListener('resize', setVh, { passive: true });
    // Also fire on orientationchange for iOS
    window.addEventListener('orientationchange', function () {
      setTimeout(setVh, 200);
    });
  } catch (e) {}

  /* ─────────────────────────────────────────────
     4. iOS SAFE AREA INSETS (notch / home bar)
     ───────────────────────────────────────────── */
  try {
    // Ensure viewport meta has viewport-fit=cover
    var vmeta = document.querySelector('meta[name="viewport"]');
    if (vmeta) {
      var content = vmeta.getAttribute('content') || '';
      if (content.indexOf('viewport-fit') === -1) {
        vmeta.setAttribute('content', content + ', viewport-fit=cover');
      }
    }
  } catch (e) {}

  /* ─────────────────────────────────────────────
     5. SMOOTH SCROLL POLYFILL
     Safari < 15.4 & Firefox (flag) don't support
     scroll-behavior: smooth natively.
     ───────────────────────────────────────────── */
  try {
    if (!('scrollBehavior' in document.documentElement.style)) {
      // Lightweight polyfill for scroll-to-top
      document.addEventListener('click', function (e) {
        var a = e.target.closest('a[href="#top"], a[href="#"], [data-scroll-top]');
        if (!a) return;
        e.preventDefault();
        lismaSmoothScroll(0, 500);
      });
    }

    function lismaSmoothScroll(targetY, duration) {
      try {
        var startY = window.scrollY || window.pageYOffset;
        var diff = targetY - startY;
        var startTime = null;
        function step(timestamp) {
          if (!startTime) startTime = timestamp;
          var progress = Math.min((timestamp - startTime) / duration, 1);
          var ease = 1 - Math.pow(1 - progress, 3); // ease-out cubic
          window.scrollTo(0, startY + diff * ease);
          if (progress < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      } catch (e) {
        window.scrollTo(0, targetY);
      }
    }
    window._lismaSmoothScroll = lismaSmoothScroll;
  } catch (e) {}

  /* ─────────────────────────────────────────────
     6. FETCH WRAPPER WITH ERROR HANDLING
     Wraps all Shopify section/cart API calls with
     timeout + retry + try-catch.
     ───────────────────────────────────────────── */
  try {
    window.limsaFetch = function (url, options, retries) {
      retries = (retries === undefined) ? 2 : retries;
      var timeout = (options && options.timeout) ? options.timeout : 8000;
      var controller = null;
      var timer = null;

      try {
        controller = new AbortController();
        timer = setTimeout(function () {
          try { controller.abort(); } catch (e) {}
        }, timeout);
      } catch (e) {
        controller = null;
      }

      var fetchOptions = Object.assign({}, options || {});
      if (controller) fetchOptions.signal = controller.signal;
      delete fetchOptions.timeout;

      return fetch(url, fetchOptions)
        .then(function (res) {
          if (timer) clearTimeout(timer);
          if (!res.ok) {
            throw new Error('HTTP ' + res.status + ' for ' + url);
          }
          return res;
        })
        .catch(function (err) {
          if (timer) clearTimeout(timer);
          var isAbort = err && err.name === 'AbortError';
          if (!isAbort && retries > 0) {
            return new Promise(function (resolve) {
              setTimeout(function () {
                resolve(window.limsaFetch(url, options, retries - 1));
              }, 600);
            });
          }
          console.warn('[Limsa] Fetch failed:', url, err && err.message);
          throw err;
        });
    };
  } catch (e) {}

  /* ─────────────────────────────────────────────
     7. INTERSECTION OBSERVER — scroll-reveal
     Polyfill-safe (falls back to showing all)
     ───────────────────────────────────────────── */
  try {
    if ('IntersectionObserver' in window) {
      var revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            try {
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            } catch (e) {}
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

      function initReveal() {
        try {
          document.querySelectorAll('[data-limsa-reveal]').forEach(function (el) {
            revealObserver.observe(el);
          });
        } catch (e) {}
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initReveal);
      } else {
        initReveal();
      }
    } else {
      // Fallback: show all reveal elements immediately
      document.querySelectorAll('[data-limsa-reveal]').forEach(function (el) {
        try { el.classList.add('is-in'); } catch (e) {}
      });
    }
  } catch (e) {}

  /* ─────────────────────────────────────────────
     8. iOS INPUT FOCUS — prevent zoom
     iOS Safari zooms in when input font-size < 16px.
     We detect focus and ensure no zoom happens.
     ───────────────────────────────────────────── */
  try {
    if (env.ios) {
      document.addEventListener('focusin', function (e) {
        var el = e.target;
        if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT')) {
          // Temporarily bump font-size to 16px while focused to prevent zoom
          if (parseFloat(window.getComputedStyle(el).fontSize) < 16) {
            el.style.fontSize = '16px';
            el.addEventListener('blur', function onBlur() {
              el.style.fontSize = '';
              el.removeEventListener('blur', onBlur);
            });
          }
        }
      }, { passive: true });
    }
  } catch (e) {}

  /* ─────────────────────────────────────────────
     9. TOUCH DRAG — prevent over-scroll bounce
     on modals / panels (iOS rubber-band fix)
     ───────────────────────────────────────────── */
  try {
    if (env.ios) {
      function preventOverscroll(el) {
        el.addEventListener('touchstart', function (e) {
          try {
            if (el.scrollTop === 0) el.scrollTop = 1;
            if (el.scrollTop + el.offsetHeight >= el.scrollHeight) {
              el.scrollTop = el.scrollHeight - el.offsetHeight - 1;
            }
          } catch (e) {}
        }, { passive: true });
      }
      // Apply to known scroll containers
      document.addEventListener('DOMContentLoaded', function () {
        try {
          var panels = document.querySelectorAll(
            '[data-limsa-sg], .limsa-sc-modal, .cart-drawer, .cart-notification'
          );
          panels.forEach(preventOverscroll);
        } catch (e) {}
      });
    }
  } catch (e) {}

  /* ─────────────────────────────────────────────
     10. FIREFOX — backdrop-filter workaround
     Firefox < 103 needs layout.css.backdrop-filter.enabled
     flag. We detect & add a fallback solid bg.
     ───────────────────────────────────────────── */
  try {
    if (env.firefox && !CSS.supports('backdrop-filter', 'blur(1px)')) {
      var ffStyle = document.createElement('style');
      ffStyle.textContent =
        '.limsa-backtop { background: rgba(11,11,11,0.9) !important; }' +
        '.limsa-sg { box-shadow: -4px 0 32px rgba(0,0,0,0.28) !important; }' +
        '.limsa-announce__badge, .limsa-new-badge { background: rgba(252,85,144,0.95) !important; }' +
        '.limsa-popup { background: rgba(250,247,242,0.98) !important; }' +
        '.limsa-hero::after { backdrop-filter: none !important; background: rgba(14,11,16,0.35) !important; }';
      document.head.appendChild(ffStyle);
    }
  } catch (e) {}

  /* ─────────────────────────────────────────────
     11. SAMSUNG BROWSER / ANDROID — fix vh
     Samsung Internet has issues with 100vh on
     certain Android versions.
     ───────────────────────────────────────────── */
  try {
    if (env.samsung || env.android) {
      // Re-run vh set after page fully loaded
      window.addEventListener('load', function () {
        try {
          var vh = window.innerHeight * 0.01;
          document.documentElement.style.setProperty('--limsa-vh', vh + 'px');
        } catch (e) {}
      });
    }
  } catch (e) {}

  /* ─────────────────────────────────────────────
     12. OPERA / MINI — fetch availability
     Opera Mini has limited fetch support.
     ───────────────────────────────────────────── */
  try {
    if (!window.fetch) {
      // Minimal XHR-based fetch shim for Opera Mini
      window.fetch = function (url, opts) {
        return new Promise(function (resolve, reject) {
          try {
            var xhr = new XMLHttpRequest();
            var method = (opts && opts.method) ? opts.method : 'GET';
            xhr.open(method, url, true);
            var headers = (opts && opts.headers) ? opts.headers : {};
            Object.keys(headers).forEach(function (k) {
              try { xhr.setRequestHeader(k, headers[k]); } catch (e) {}
            });
            xhr.onload = function () {
              resolve({
                ok: xhr.status >= 200 && xhr.status < 300,
                status: xhr.status,
                json: function () { return Promise.resolve(JSON.parse(xhr.responseText)); },
                text: function () { return Promise.resolve(xhr.responseText); }
              });
            };
            xhr.onerror = function () { reject(new Error('Network error')); };
            xhr.send((opts && opts.body) ? opts.body : null);
          } catch (e) { reject(e); }
        });
      };
    }
  } catch (e) {}

  /* ─────────────────────────────────────────────
     13. IMAGE LOADING — lazy fallback for old Safari
     ───────────────────────────────────────────── */
  try {
    if (!('loading' in HTMLImageElement.prototype)) {
      // Polyfill native lazy loading for old Safari
      var lazyImages = document.querySelectorAll('img[loading="lazy"]');
      if ('IntersectionObserver' in window) {
        var imgObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              try {
                var img = entry.target;
                if (img.dataset.src) img.src = img.dataset.src;
                imgObserver.unobserve(img);
              } catch (e) {}
            }
          });
        });
        lazyImages.forEach(function (img) { imgObserver.observe(img); });
      } else {
        // Oldest fallback: load all
        lazyImages.forEach(function (img) {
          try { if (img.dataset.src) img.src = img.dataset.src; } catch (e) {}
        });
      }
    }
  } catch (e) {}

  /* ─────────────────────────────────────────────
     14. CONSOLE SUPPRESSION in production
     Swallow non-critical errors gracefully
     ───────────────────────────────────────────── */
  try {
    var origError = console.error.bind(console);
    console.error = function () {
      var args = Array.prototype.slice.call(arguments);
      var msg = args[0] ? String(args[0]) : '';
      // Suppress known Shopify/Evoluxia noise
      var noise = ['Slick', 'jQuery', 'slick', 'Cannot read prop', 'shopify_pay'];
      var isNoise = noise.some(function (n) { return msg.indexOf(n) !== -1; });
      if (!isNoise) origError.apply(console, args);
    };
  } catch (e) {}

})();

/* ── Header: add shadow class on scroll (mobile) ── */
try {
  var hdr = document.querySelector('.header-wrapper');
  if (hdr) {
    window.addEventListener('scroll', function(){
      hdr.classList.toggle('is-scrolled', window.scrollY > 10);
    }, { passive: true });
  }
} catch(e) {}
